import { type BackupRecording, type Chunk, type SessionChunk } from './contracts';
import { type BackupSlice } from './useSessionRecordingStore/backupSlice';
import { type ChunkSlice } from './useSessionRecordingStore/chunkSlice';
import { type ConnectivitySlice } from './useSessionRecordingStore/connectivitySlice';
import { type HydrationSlice } from './useSessionRecordingStore/hydrationSlice';
import { type BackupRecorder } from './useSessionRecordingStore/lib/BackupRecorder';
import { type ChunkRecorder } from './useSessionRecordingStore/lib/ChunkRecorder';
import { type RecordingSlice } from './useSessionRecordingStore/recordingSlice';
import { type TaskQueueSlice } from './useSessionRecordingStore/taskQueueSlice';

// The inert contract closure lives in `@heidi/capture/contracts` and
// `SessionRecordingConfig` in `@heidi/capture/ports`. Re-exported here so legacy
// type paths keep compiling against the identical type identities.
export {
  type BackupRecording,
  type BackupToUpload,
  type BlobStatus,
  type Chunk,
  type ChunkDuration,
  type ChunkSpec,
  type Codec,
  type Format,
  type SessionChunk,
} from './contracts';
export { type SessionRecordingConfig } from './ports';

export type Status = 'READY' | 'RECORDING' | 'ERROR';

export interface DictationChunk extends Chunk {
  dictationId: string;
  dictationRecordingId: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LogFn = (message: string, ...args: any[]) => void;

export interface Logger {
  info: LogFn;
  error: LogFn;
  warn: LogFn;
}

export interface Recording {
  id: string;
  /** Canonical Heidi user reference that owned this recording. */
  userRefId?: string;
  chunks: SessionChunk[];
  backupRecording: BackupRecording | null;
  startDate: Date;
  endDate?: Date;
  lastChunkIndex: number | null;
  useTranscriptionServiceV2?: boolean;
}

export interface Session {
  id: string;
  recordings: Recording[];
}

export interface SessionRecording {
  showModal: boolean;
  stream: MediaStream | null;
  selectDevice: (deviceId: string) => void;
  start: (sessionId: string) => Promise<string>;
  stop: (sessionId: string) => void;
}

// Each sets `name` so telemetry survives minification: the bundler mangles the
// class name to a single letter, but a string literal is preserved, and
// `error.name` (not `error.constructor.name`) then reports it in production.
export class MicPermissionError extends Error {
  name = 'MicPermissionError';
}
export class DeviceInUseError extends Error {
  name = 'DeviceInUseError';
}
export class NoMicDevicesError extends Error {
  name = 'NoMicDevicesError';
}
export class StartRecordingFailed extends Error {
  name = 'StartRecordingFailed';
}
export class AudioStorageRecoveryFailed extends StartRecordingFailed {
  name = 'AudioStorageRecoveryFailed';
}
export class RecordingActiveInAnotherTabError extends StartRecordingFailed {
  name = 'RecordingActiveInAnotherTabError';
}
export class StartRecordingCancelled extends Error {
  name = 'StartRecordingCancelled';
}
export class CreateRecordingIdFailed extends Error {
  name = 'CreateRecordingIdFailed';
}
export class StopRecordingFailed extends Error {
  name = 'StopRecordingFailed';
}
export class ChunkRecordingFailed extends Error {
  name = 'ChunkRecordingFailed';
}
export class NoAudioDeviceError extends Error {
  name = 'NoAudioDeviceError';
}
// Named so telemetry can tell it apart; the rest report as plain `Error`.
export class TooLowStorageError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = 'TooLowStorageError';
  }
}
export class DexieStartError extends Error {
  name = 'DexieStartError';
}
export class TabSharingError extends Error {
  name = 'TabSharingError';
}

export interface RecordingObjects {
  chunkRecorder: ChunkRecorder;
  backupRecorder: BackupRecorder;
}

export interface AvailableDevice {
  id: string;
  label: string;
}

export interface StreamListener {
  id: string;
  onInterruption?: () => void;
}

export type SessionRecordingStore = TaskQueueSlice &
  RecordingSlice &
  ChunkSlice &
  BackupSlice &
  HydrationSlice &
  ConnectivitySlice;
